/// <reference path="../../../../../typings/jquery.d.ts"/>

import { User } from '../../../../../../shared/models/api/User/User';
import { AppPersistenceService } from '../../services/AppPersistenceService';
import { AppViewService } from '../../services/AppViewService';
import { AppValidationService } from '../../services/AppValidationService';
import { AppApiService, VehicleUpdateChanges } from '../../services/AppApiService';
import { AppRouterService } from '../../services/AppRouterService';
import { BasicErrorService } from '../../../../../../shared/services/BasicErrorService';
import { AppFormModel } from '../../models/AppFormModel';
import { GarageTypeApi } from '../../../../../../shared/models/generated/FahrzeugdatenGarageTypeApi';
import { FahrzeugModel } from '../../../../../../shared/models/view/Fahrzeug';
import { AppModelAdapter } from '../../models/AppModelAdapter';
import { AppUtils } from '../../../../../../shared/utils/index';


export class InputPageController implements StepController, StandardViewEvents, FindCarViewEvents, InputPageEvents {
	private readonly $ctx: JQuery;
	private readonly appApiService: AppApiService;
	private readonly applicationRouter: AppRouterService;
	private readonly basicErrorService: BasicErrorService<BasicAppDefinition>;
	private readonly user: User;
	private readonly isEditMode: boolean;
	private readonly options: BasicAppOptions<MyVehicleAppDefinition>;
	private readonly appViewService: AppViewService;
	private readonly appPersistenceService: AppPersistenceService;
	private readonly parent: AppController;
	private readonly loader: BasicLoader<MyVehicleAppDefinition>;
	private appValidationService?: AppValidationService;
	private readonly kfzId: string;
	private isManuallyChanged: boolean;
	private readonly buildingSeriesId: string;
	private readonly modellVariantId: string;
	private readonly vehicleId: string;

	constructor(parent: AppController, options: BasicAppOptions<MyVehicleAppDefinition>, loader: BasicLoader<MyVehicleAppDefinition>, user: User, isEditMode: boolean, kfzId?: string) {
		this.parent = parent;
		this.options = options;
		this.loader = loader;
		this.user = user;
		this.$ctx = options.$ctx;
		this.isEditMode = isEditMode;
		this.kfzId = kfzId;
		this.buildingSeriesId = '#buildingseriesgeneration';
		this.modellVariantId = '#modell';
		this.vehicleId = '#vehicle';
		this.applicationRouter = new AppRouterService(options);
		this.appPersistenceService = new AppPersistenceService(options);
		this.appViewService = new AppViewService(parent, options, loader);
		this.basicErrorService = new BasicErrorService(options);
		this.appApiService = new AppApiService(parent, options);
	}

	public start(): void {
		this.isManuallyChanged = false;
		const viewConfig = {
			data: this.getViewOptions(),
			templateurl: this.options.app.currenttemplate.templateurl,
			campaignurls: this.options.app.campaignurls,
			parent: this,
			$ctx: this.$ctx,
			options: this.options,
			callback: this.afterRenderApp
		};
		this.appViewService.render(viewConfig);
	}

	private getViewOptions(): { [key: string]: any } {
		const formModel = this.appPersistenceService.getFormModelFromStore() || new AppFormModel();
		return {
			BaureihenGenerationen: formModel.BaureihenGenerationen,
			Modellvarianten: formModel.ModellVarianten,
			Fahrzeuge: formModel.Fahrzeuge,
			Fahrzeug: formModel.Fahrzeug,
			IsEditMode: this.isEditMode,
			User: this.user,
			Apis: {
				apibrandsv2: this.options.app.apibrandsv2,
				apibuildinggenerationv2: this.options.app.apibuildinggenerationv2,
				apimodelvariantsv2: this.options.app.apimodelvariantsv2,
				apiseriesv2: this.options.app.apiseriesv2,
				apivehiclev2: this.options.app.apivehiclev2,
			},
			nexturl: this.options.app.nexturl || '',
			prevurl: this.options.app.prevurl || ''
		};
	}

	public afterRenderApp(): void {
		this.loader.loadSubApps({ options: this.options }, () => {
			AppUtils.Helper.scheduleTask(() => {
				T.Utils.Tracking.pushKameleoonData();
			}, 'background');

			this.appValidationService = new AppValidationService({ options: this.options }, this.appViewService.getForm());
			this.parent.afterRenderApp();

			const formModel = this.appPersistenceService.getFormModelFromStore();
			this.appViewService.setFormModelToView(formModel);

			this.appViewService.addAutocompleteListeners(this.onBrandAutocomplete.bind(this), this.onSeriesAutocomplete.bind(this));

			this.appViewService.addListeners(this);
			this.appViewService.addInputPageListeners(this);

			this.appViewService.addFindCarListeners(this);

			this.updateValidationByVehicletype();

			if (this.isEditMode) {
				const urlParams = AppUtils.Url.getParamsAsSingle(window.location.search),
					vehicleID = urlParams.myVehicleId || urlParams.id;
				this.appViewService.updateAutokatalogUrls(vehicleID);
			}

			AppUtils.View.stopLoader();
			AppUtils.View.scrollTop();
		});
	}

	public onBrandAutocomplete(e: JQuery.TriggeredEvent, term: string, element: JQuery): void {
		if (element && element.length) {
			const formModel = this.appPersistenceService.getFormModelFromStore();
			formModel.Fahrzeug.Marke = element.data('js-val');
			// eslint-disable-next-line eqeqeq
			if (undefined != formModel.Fahrzeug.Marke) { //NOSONAR ensure no implicit number conversion
				formModel.Fahrzeug.Marke = formModel.Fahrzeug.Marke.toString();
			}
			formModel.Fahrzeug.MarkeId = element.data('keyid') || element.data('js-id');
			formModel.Fahrzeug.TetId = element.data('tetid');
			// eslint-disable-next-line eqeqeq
			if (undefined != formModel.Fahrzeug.TetId) { //NOSONAR ensure no implicit number conversion
				formModel.Fahrzeug.TetId = formModel.Fahrzeug.TetId.toString();
			}
			this.appPersistenceService.setFormModelToStore(formModel);
		}
	}

	public onSeriesAutocomplete(e: JQuery.TriggeredEvent, term: string, element: JQuery): void {
		if (element && element.length) {
			const formModel = this.appPersistenceService.getFormModelFromStore();
			formModel.Fahrzeug.Baureihe = element.data('js-val');
			// eslint-disable-next-line eqeqeq
			if (undefined != formModel.Fahrzeug.Baureihe) { //NOSONAR ensure no implicit number conversion
				formModel.Fahrzeug.Baureihe = formModel.Fahrzeug.Baureihe.toString();
			}
			formModel.Fahrzeug.BaureiheId = element.data('keyid') || element.data('js-id');
			formModel.Fahrzeug.TetId = element.data('tetid');
			// eslint-disable-next-line eqeqeq
			if (undefined != formModel.Fahrzeug.TetId) { //NOSONAR ensure no implicit number conversion
				formModel.Fahrzeug.TetId = formModel.Fahrzeug.TetId.toString();
			}
			this.appPersistenceService.setFormModelToStore(formModel);
			this.appViewService.updateModelSearchURL(formModel);
		}
	}

	public vehicletypeListener(event: JQuery.Event): void {
		AppUtils.Helper.scheduleTask(() => {
			if (event && (event as JQuery.TriggeredEvent).target) {
				const actionUrl = $((event as JQuery.TriggeredEvent).target).data('action-url');
				if (actionUrl) {
					event.preventDefault();
					this.applicationRouter.routeToLocation(actionUrl);
					return;
				}
			}
			this.updateValidationByVehicletype();
		}, 'user-visible');
	}

	public openDialogListener(event: JQuery.Event): void {
		if (event) {
			event.preventDefault();
		}
		const formModel = this.appPersistenceService.getFormModelFromStore();

		AppUtils.View.startLoader();

		const openPopup = (changes: VehicleUpdateChanges): void => {
			this.appPersistenceService.setFormModelToStore(formModel);
			if (formModel.Fahrzeug.BaureiheId
				&& (changes.Marke || changes.Baureihe || !formModel.BaureihenGenerationen || !formModel.BaureihenGenerationen.length)) {
				this.loadBaureihenGenerationSuggestions(formModel);
			}
			if (formModel.Fahrzeug.BaureiheGenerationId) {
				const setVal = (): void => {
					if (formModel.Fahrzeug.ModellId) {
						this.appViewService.setValueAndEnableLayerField('Modellvariante', formModel.Fahrzeug.ModellId);
					}
				};
				if (formModel.ModellVarianten && formModel.ModellVarianten.length) {
					setVal();
				} else {
					this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
					this.loadModellVariantSuggestions(formModel, () => {
						setVal();
					});
				}
			} else {
				this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
			}
			if (formModel.Fahrzeug.ModellId) {
				const setVal = (): void => {
					this.appViewService.setValueAndEnableLayerField('Vehicle', formModel.Fahrzeug.KfzId);
				};
				if (formModel.Fahrzeuge && formModel.Fahrzeuge.length) {
					this.appViewService.fillVehicleSelections(formModel.Fahrzeuge);
					setVal();
				} else {
					this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);
					this.loadVehicleSuggestions(formModel, () => {
						setVal();
					});
				}
			} else {
				this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);
			}

			AppUtils.View.stopLoader();
			$.magnificPopup.open({
				modal: false,
				mainClass: 'l-lightbox',
				items: {
					src: '#vehicle_man',
					type: 'inline'
				}
			});
		};

		if (formModel.Fahrzeug) {
			this.appApiService.updateVehicleDetailIds(formModel.Fahrzeug, (changes) => {
				openPopup(changes);
			}, (errorSource, changes) => {
				console.warn(`Error updating ${errorSource}`);
				openPopup(changes);
			});
		}

	}

	public saveDialogListener(_event: JQuery.Event): void {
		let formModel = this.appPersistenceService.getFormModelFromStore();
		formModel = this.appViewService.getFormModelFromView(formModel);
		formModel.Fahrzeug.IsManuallyChanged = true;
		this.isManuallyChanged = true;
		this.appPersistenceService.setFormModelToStore(formModel);
		this.appViewService.updatePkwViewparts(formModel);
		this.appViewService.hideCarDetails();
	}

	public viewChangeListener(_event: JQuery.Event): void {
		AppUtils.Helper.scheduleTask(() => {
			let formModel = this.appPersistenceService.getFormModelFromStore();
			formModel = this.appViewService.getFormModelFromView(formModel);
			formModel.Fahrzeug.IsManuallyChanged = this.isManuallyChanged;
			if (this.kfzId && !formModel.Fahrzeug.KfzId) {
				formModel.Fahrzeug.KfzId = this.kfzId;
			}
			this.appPersistenceService.setFormModelToStore(formModel);
		}, 'user-visible');
	}

	public submitListener(event: JQuery.Event): void {
		event.preventDefault();
		this.appViewService.showApiErrorMessage(false);

		if (this.appValidationService && this.appValidationService.isValid()) {
			AppUtils.View.startLoader();

			const formModel = this.appPersistenceService.getFormModelFromStore();
			const vehicletype = this.appViewService.getSelectedVehicletype();

			if (vehicletype === GarageTypeApi.Pkw) {
				this.saveVehicle(formModel);
			} else {
				if (formModel.Fahrzeug.KfzId && !formModel.Fahrzeug.IsManuallyChanged) {
					this.saveVehicle(formModel);
				} else if (this.isVehicleTeTCompatible(formModel.Fahrzeug)) {
					const modelUrl = this.appViewService.getModelSearchUrlFromView();
					const apiUrl = AppUtils.Url.updateUrlParameter(
						{
							Brand: formModel.Fahrzeug.Marke,
							Series: formModel.Fahrzeug.Baureihe,
							SeriesId: formModel.Fahrzeug.BaureiheId,
							Status: AppModelAdapter.getDefaultMarkenStatus(),
							Repository: AppModelAdapter.getFahrzeugdatenRepository(formModel.Fahrzeug),
							searchname: formModel.Fahrzeug.Modell
						},
						modelUrl);

					this.appApiService.getModels(apiUrl,
						(data) => {
							if (!data || !data.Data) {
								this.displayApiNotAvailable();
							} else {
								if (data.Data.Modelle && data.Data.Modelle.length > 0 && data.Data.Modelle[0].Id) {
									formModel.Fahrzeug.TetId = data.Data.Modelle[0].Id;
								}
								this.saveVehicle(formModel);
							}
						},
						() => {
							this.displayApiNotAvailable();
						});
				} else {
					this.saveVehicle(formModel);
				}
			}
		}
	}

	private isVehicleTeTCompatible(model: FahrzeugModel): boolean {
		return !!model.Marke && !!model.Baureihe && !!model.BaureiheId && !!model.Modell;
	}

	private displayApiNotAvailable(): void {
		AppUtils.View.stopLoader();
		this.appViewService.showApiErrorMessage();
	}

	private saveVehicle(formModel: AppFormModel): void {
		this.appApiService.submitRequest(formModel, this.user
			, () => {
				AppUtils.View.stopLoader();
				this.renderSuccessTemplate();
			}
			, (jqxhr) => {
				this.handleErrorResponse(jqxhr);
			});
	}

	private updateValidationByVehicletype(): void {
		const vehicletype = this.appViewService.getSelectedVehicletype();

		let formModel = this.appPersistenceService.getFormModelFromStore();
		if (formModel.Fahrzeug && !isNaN(formModel.Fahrzeug.Fahrzeugart) && formModel.Fahrzeug.Fahrzeugart !== vehicletype) {
			const model = new AppFormModel();
			model.Fahrzeug = new FahrzeugModel();
			model.Fahrzeug.Fahrzeugart = vehicletype;
			model.Fahrzeug.FahrzeugTitel = formModel.Fahrzeug.FahrzeugTitel;
			model.Fahrzeug.Farbe = formModel.Fahrzeug.Farbe;
			model.Fahrzeug.KennzeichenPrefix = formModel.Fahrzeug.KennzeichenPrefix;
			model.Fahrzeug.KennzeichenSuffix = formModel.Fahrzeug.KennzeichenSuffix;
			formModel = model;
			this.appPersistenceService.setFormModelToStore(formModel);
			this.start();
			return;
		}
		if (formModel.Fahrzeug) {
			formModel.Fahrzeug.Fahrzeugart = vehicletype;
		}
		this.appViewService.setVehicleType(formModel);
	}

	/**
	 * If the sucess page is no real application page, simulate success step to treat it equal to other steps.
	 */
	private renderSuccessTemplate(): void {
		this.appPersistenceService.clearApplicationSession();
		this.applicationRouter.success();
	}

	private handleErrorResponse(jqxhr: JQueryXHR): void {
		AppUtils.View.stopLoader();
		if (this.basicErrorService.isServerValidationError(jqxhr) && this.appValidationService) {
			this.appValidationService.handleServerValidationErrors(jqxhr);
			this.appValidationService.triggerValidation();
		} else {
			this.displayApiNotAvailable();
		}
	}

	public onFocusInBrandListener(): void {
		const formModel = this.appPersistenceService.getFormModelFromStore();

		this.appViewService.updateBrandsSearchURL(formModel);
	}

	public onFocusInFamilyListener(): void {
		const formModel = this.appViewService.getFormModelFromView(this.appPersistenceService.getFormModelFromStore());
		this.appViewService.updateFamilySearchURL(formModel);
	}

	public onChangeBrandListener(): void {
		const formModel = this.appViewService.getFormModelFromView(this.appPersistenceService.getFormModelFromStore());
		if (formModel.Fahrzeug.Fahrzeugart === GarageTypeApi.Pkw) {
			this.getMarkeEnableFileds(formModel, () => {
				this.appViewService.resetFamilyData(formModel);
				this.appViewService.removeAllOptionExceptFirstAndDisableField(".js-building-series-generation", true);
				this.appViewService.removeAllOptionExceptFirstAndDisableField(".js-model-series", true);
				this.appViewService.removeAllOptionExceptFirstAndDisableField(".js-vehicles", true);
				this.appPersistenceService.setFormModelToStore(formModel);
			});
		}
	}

	private getMarkeEnableFileds(formModel: AppFormModel, cb: () => void): void {
		this.appViewService.enableFamilyData(false);

		if (formModel.Fahrzeug.Marke) {
			this.appApiService.getMarke(formModel,
				this.handleOnMarkeSuccess.bind(this, formModel, cb),
				this.handleOnMarkeError.bind(this, formModel)
			);
		}
	}

	private handleOnMarkeSuccess(formModel: AppFormModel, cb: () => void, data: HerstellerWithKeyErgebnisApi): void {
		const brand = data.Marken[0];
		if (brand && brand.Id) {
			formModel.Fahrzeug.MarkeId = brand.Id;
			formModel.Fahrzeug.TetId = brand.Key;
		}
		else {
			formModel.Fahrzeug.MarkeId = '';
			formModel.Fahrzeug.TetId = '';
		}

		formModel.BaureihenGenerationen = [];
		formModel.ModellVarianten = [];
		formModel.Fahrzeuge = [];
		this.appPersistenceService.setFormModelToStore(formModel);
		this.appViewService.enableFamilyData(true);
		cb();
	}

	private handleOnMarkeError(formModel: AppFormModel): void {
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.buildingSeriesId, true);
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);

		formModel.BaureihenGenerationen = [];
		formModel.ModellVarianten = [];
		formModel.Fahrzeuge = [];
		this.appViewService.enableFamilyData(true);
		this.appPersistenceService.setFormModelToStore(formModel);
	}

	public onChangeFamilyListener(): void {
		const formModel = this.appViewService.getFormModelFromView(this.appPersistenceService.getFormModelFromStore());
		if (formModel.Fahrzeug.Fahrzeugart === GarageTypeApi.Pkw) {
			this.appViewService.removeAllOptionExceptFirstAndDisableField(this.buildingSeriesId, true);
			this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
			this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);
			this.appApiService.getBaureiheId(formModel,
				this.handleOnBaureiheSuccess.bind(this, formModel),
				this.handleOnBaureiheAndBaureihengenerationError.bind(this, formModel)
			);
		}
	}


	private handleOnBaureiheSuccess(formModel: AppFormModel, data: BaureihenWithKeyErgebnisApi): void {
		formModel.Fahrzeug.BaureiheId = data.Baureihen[0].Id;
		formModel = this.appViewService.getFormModelFromView(formModel);
		this.loadBaureihenGenerationSuggestions(formModel);
		this.appPersistenceService.setFormModelToStore(formModel);
	}

	private loadBaureihenGenerationSuggestions(formModel: AppFormModel): void {
		if (this.appViewService.checkEnableBaureihenGenerationSelection()) {
			this.appViewService.clearBaureiheGenerationenSelect();

			this.getBaureihengenerationAndSetSelections(formModel, () => {
				this.appViewService.fillBaureihenGenerationSelections(formModel.BaureihenGenerationen, formModel.Fahrzeug.BaureiheGenerationId);
				this.appPersistenceService.setFormModelToStore(formModel);
			});
		}

	}

	private handleOnBaureiheAndBaureihengenerationError(formModel: AppFormModel): void {
		formModel.BaureihenGenerationen = [];
		formModel.ModellVarianten = [];
		formModel.Fahrzeuge = [];
		this.appPersistenceService.setFormModelToStore(formModel);
	}

	private getBaureihengenerationAndSetSelections(formModel: AppFormModel, cb: () => void): void {
		this.appApiService.getBaureihenGenerationenSelections(
			formModel,
			this.handleOnBaureihengenerationSuccess.bind(this, formModel, cb),
			this.handleOnBaureiheAndBaureihengenerationError.bind(this, formModel)
		);
	}

	private handleOnBaureihengenerationSuccess(formModel: AppFormModel, cb: () => void, data: BaureiheGenerationenWithKeyErgebnisApi): void {
		formModel.BaureihenGenerationen = data.BaureiheGenerationen;
		this.appPersistenceService.setFormModelToStore(formModel);
		cb();
	}

	public onBaureiheGenerationenChangeListener(): void {
		let formModel = this.appPersistenceService.getFormModelFromStore();
		formModel = this.appViewService.getFormModelFromView(formModel);
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);
		this.loadModellVariantSuggestions(formModel, () => { /* ignore */ });
	}

	private loadModellVariantSuggestions(formModel: AppFormModel, cb: () => void): void {
		this.appApiService.getModellVariantenSelections(
			formModel,
			this.handleOnModellvariantenSuccess.bind(this, formModel, cb),
			this.handleOnModellvariantenError.bind(this, formModel)
		);
	}

	private handleOnModellvariantenSuccess(formModel: AppFormModel, cb: () => void, data: ModellVariantenWithKeyErgebnisApi): void {
		formModel.ModellVarianten = data.ModellVarianten;
		this.appViewService.fillModellVariantenSelections(formModel.ModellVarianten);
		this.appPersistenceService.setFormModelToStore(formModel);
		cb();
	}

	private handleOnModellvariantenError(formModel: AppFormModel): void {
		formModel.ModellVarianten = [];
		formModel.Fahrzeuge = [];
		this.appPersistenceService.setFormModelToStore(formModel);
	}

	public onModellVarianteChangeListener(): void {
		let formModel = this.appPersistenceService.getFormModelFromStore();
		formModel = this.appViewService.getFormModelFromView(formModel);
		this.loadVehicleSuggestions(formModel, () => { /* ignore */ });
	}

	private loadVehicleSuggestions(formModel: AppFormModel, cb: () => void): void {
		this.appApiService.getVehicleSelection(
			formModel,
			this.handleOnVehiclesSuccess.bind(this, formModel, cb),
			this.handleOnVehiclesError.bind(this, formModel)
		);
	}

	private handleOnVehiclesSuccess(formModel: AppFormModel, cb: () => void, data: FahrzeugeWithKeyErgebnisApi): void {
		formModel.Fahrzeuge = data.Fahrzeuge;
		this.appViewService.fillVehicleSelections(formModel.Fahrzeuge);
		this.appPersistenceService.setFormModelToStore(formModel);
		cb();
	}

	private handleOnVehiclesError(formModel: AppFormModel): void {
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.modellVariantId, true);
		this.appViewService.removeAllOptionExceptFirstAndDisableField(this.vehicleId, true);

		formModel.Fahrzeuge = [];
		this.appPersistenceService.setFormModelToStore(formModel);
	}
}
